import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import logoImg from '../../images/logo-no-tagline.png';
import iconImg from '../../images/icon_transparent.png';

const ROTATING_LINES = [
  "remember to text the people who matter — before life gets in the way.",
  "f8 helps you notice your own patterns in how you connect.",
  "small circles and real conversations. no endless feed.",
  "a calmer way to keep up with the people you care about.",
];

const Login = () => {
  const [mode, setMode] = useState('welcome'); // welcome | email | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [message, setMessage] = useState('');
  const [qIdx, setQIdx] = useState(0);

  const { login, signInWithGoogle, resetPassword, error, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const isRelative = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//');
  const from = isRelative ? redirectParam : (location.state?.from?.pathname || '/');
  // PPL-416 — a draft typed before signing in (the desk's "sending your first
  // message signs you in") comes back as state.initialMessage so the page it
  // returns to can prefill its composer. Never auto-sent.
  const initialMessage = typeof location.state?.initialMessage === 'string' && location.state.initialMessage
    ? location.state.initialMessage
    : null;
  const returnState = initialMessage ? { initialMessage } : undefined;

  useEffect(() => {
    if (user) navigate(from, { replace: true, state: initialMessage ? { initialMessage } : undefined });
  }, [user, navigate, from, initialMessage]);

  useEffect(() => {
    const t = setInterval(() => setQIdx(i => (i + 1) % ROTATING_LINES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const handleGoogleSignIn = async () => {
    setMessage('');
    try {
      await signInWithGoogle();
      navigate(from, { replace: true, state: returnState });
    } catch (err) {
      console.error('Google sign-in error:', err);
    }
  };

  const handleEmailLogin = async (e) => {
    e?.preventDefault?.();
    setMessage('');
    try {
      await login(email, password);
      navigate(from, { replace: true, state: returnState });
    } catch (err) {
      console.error('Email login error:', err);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault?.();
    setMessage('');
    try {
      await resetPassword(resetEmail);
      setMessage('password reset email sent. check your inbox.');
      setMode('email');
      setResetEmail('');
    } catch (err) {
      console.error('Password reset error:', err);
    }
  };

  const handleGuest = () => navigate('/f8chat', { replace: true });

  return (
    <div className="login-root" style={{
      position: 'fixed', inset: 0,
      background: '#0e0817',
      color: '#fff',
      fontFamily: 'inherit',
      textAlign: 'left',
      zIndex: 1000,
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* LEFT — brand canvas (desktop only) */}
      <div className="login-brand" style={{
        position: 'relative', overflow: 'hidden',
        padding: '40px 56px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        background: 'linear-gradient(180deg, #1a0e22 0%, #0e0817 100%)',
      }}>
        <Aurora />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <img src={logoImg} alt="pplkpr" style={{ height: 40, width: 'auto', display: 'block' }} />
        </div>

        <div style={{ position: 'relative', maxWidth: 540 }}>
          <BigOrb />
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 12px 6px 8px', borderRadius: 999,
            background: 'rgba(217, 70, 239, 0.12)',
            border: '1px solid rgba(217, 70, 239, 0.28)',
            color: '#f0abfc', fontSize: 11.5, fontWeight: 600,
            marginBottom: 24, position: 'relative', zIndex: 2,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: '#34d399',
              boxShadow: '0 0 8px #34d399',
            }} />
            <span>now with f8 — your relationship co-pilot</span>
          </div>
          <h1 style={{
            margin: 0, position: 'relative', zIndex: 2,
            fontSize: 'clamp(56px, 6.4vw, 92px)', lineHeight: 0.94,
            letterSpacing: '-0.045em', fontWeight: 800,
          }}>
            a kinder corner<br/>
            of the{' '}
            <span style={{
              background: 'linear-gradient(135deg, #f0abfc, #d946ef 40%, #a855f7)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              color: 'transparent',
            }}>internet.</span>
          </h1>
          <p style={{
            margin: '24px 0 0', maxWidth: 460, position: 'relative', zIndex: 2,
            color: 'rgba(232, 220, 250, 0.78)', fontSize: 17, lineHeight: 1.55,
          }}>
            real people. small circles. an ai friend named f8.<br/>
            moderation that has your back — offensive content blocked, suspicious messages flagged.
          </p>
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24 }}>
          <div style={{
            maxWidth: 380, padding: 18, borderRadius: 16,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              {ROTATING_LINES.map((_, i) => (
                <span key={i} style={{
                  height: 2, flex: i === qIdx ? '0 0 24px' : '0 0 12px',
                  borderRadius: 999,
                  background: i === qIdx ? 'linear-gradient(90deg, #f0abfc, #d946ef)' : 'rgba(255,255,255,0.12)',
                  transition: 'all 0.4s',
                }} />
              ))}
            </div>
            <p key={qIdx} style={{
              margin: 0, color: '#fff', fontSize: 15, lineHeight: 1.5, fontWeight: 500,
              animation: 'loginFadeIn 0.5s ease',
            }}>{ROTATING_LINES[qIdx]}</p>
          </div>
        </div>
      </div>

      {/* RIGHT — auth panel */}
      <div className="login-auth" style={{
        position: 'relative', overflow: 'auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background:
          'radial-gradient(700px 400px at 50% 30%, rgba(124, 58, 237, 0.15), transparent 60%), ' +
          'rgba(14, 8, 23, 0.6)',
        backdropFilter: 'blur(8px)',
      }}>
        <div className="login-auth-inner" style={{ width: '100%', maxWidth: 380 }}>
          <div className="login-mobile-logo">
            <img src={logoImg} alt="pplkpr" />
          </div>
          {mode === 'welcome' && (
            <Welcome
              onEmail={() => setMode('email')}
              onGoogle={handleGoogleSignIn}
              onGuest={handleGuest}
              loading={loading}
              error={error}
              message={message}
            />
          )}
          {mode === 'email' && (
            <EmailForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onSubmit={handleEmailLogin}
              onBack={() => setMode('welcome')}
              onForgot={() => { setResetEmail(email); setMode('reset'); }}
              loading={loading}
              error={error}
              message={message}
            />
          )}
          {mode === 'reset' && (
            <ResetForm
              resetEmail={resetEmail}
              setResetEmail={setResetEmail}
              onSubmit={handleResetPassword}
              onBack={() => setMode('email')}
              loading={loading}
              error={error}
            />
          )}

          <div style={{
            marginTop: 32, paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,0.05)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            color: 'rgba(220,200,240,0.45)', fontSize: 11.5,
          }}>
            <span>© pplkpr 2026</span>
            <div style={{ display: 'flex', gap: 16 }}>
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>privacy</a>
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>terms</a>
              <Link to="/faq" style={{ color: 'inherit', textDecoration: 'none' }}>help</Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes loginFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes loginOrbDrift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.05); }
        }
        @keyframes loginOrbDrift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, 30px) scale(0.95); }
        }

        /* mobile-first: single column, brand hidden, compact padding */
        .login-root { display: block; height: 100vh; height: 100dvh; overflow: auto; }
        .login-brand { display: none; }
        .login-auth { min-height: 100%; padding: 28px 20px calc(28px + env(safe-area-inset-bottom)); }
        .login-auth-inner { max-width: 380px; margin: 0 auto; }
        .login-mobile-logo { display: flex; justify-content: center; margin: 4px 0 24px; }
        .login-mobile-logo img { height: 32px; width: auto; display: block; }
        .login-icon-img { width: 56px; height: 56px; }
        .login-h2 { font-size: 26px; }

        /* tablet+: roomier */
        @media (min-width: 600px) {
          .login-auth { padding: 40px 32px; }
          .login-icon-img { width: 64px; height: 64px; }
          .login-h2 { font-size: 30px; }
        }

        /* desktop: two-column with brand canvas */
        @media (min-width: 900px) {
          .login-root {
            display: grid;
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
            height: 100vh; height: 100dvh;
            overflow: hidden;
          }
          .login-brand { display: flex; }
          .login-auth { border-left: 1px solid rgba(255,255,255,0.04); padding: 40px 32px; }
          .login-mobile-logo { display: none; }
        }
      `}</style>
    </div>
  );
};

// ─── Welcome ────────────────────────────────────────────────
function Welcome({ onEmail, onGoogle, onGuest, loading, error, message }) {
  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <img src={iconImg} alt="" className="login-icon-img" style={{ display: 'block', marginBottom: 14 }} />
        <div style={{ fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>welcome to pplkpr</div>
      </div>

      <h2 className="login-h2" style={{ margin: 0, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
        sign in
      </h2>
      <p style={{ margin: '6px 0 28px', color: 'rgba(220,200,240,0.55)', fontSize: 13.5 }}>
        we&apos;ll keep you signed in across devices.
      </p>

      <button onClick={onGoogle} disabled={loading} style={{
        appearance: 'none', border: 0, cursor: loading ? 'default' : 'pointer',
        width: '100%', padding: '13px 16px', borderRadius: 14,
        background: '#fff', color: '#1a0e22',
        fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        boxShadow: '0 6px 20px rgba(255,255,255,0.08)',
        opacity: loading ? 0.7 : 1,
      }}>
        <GoogleIcon />
        <span>{loading ? 'please wait...' : 'continue with google'}</span>
      </button>

      <div style={{
        margin: '22px 0', display: 'flex', alignItems: 'center', gap: 12,
        color: 'rgba(220,200,240,0.4)', fontSize: 11, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '0.1em',
      }}>
        <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
        <span>or</span>
        <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
      </div>

      <button onClick={onEmail} style={{
        appearance: 'none', cursor: 'pointer',
        width: '100%', padding: '13px 16px', borderRadius: 14,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="#e8dcfa" strokeWidth="1.7"/>
          <path d="m4 7 8 6 8-6" stroke="#e8dcfa" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>log in with email</span>
      </button>
      {/* PPL-36 — sign-up-with-email entry from the welcome screen. */}
      <div style={{ marginTop: 12, fontSize: 12.5, color: 'rgba(220,200,240,0.6)', textAlign: 'center' }}>
        new to pplkpr?{' '}
        <Link to="/register" style={{
          color: '#a78bfa', fontWeight: 600, textDecoration: 'none',
        }}>sign up with email →</Link>
      </div>

      <button onClick={onGuest} style={{
        appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent',
        width: '100%', marginTop: 18,
        color: '#a78bfa', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        whiteSpace: 'nowrap',
      }}>
        <span>or look around as a guest</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {error && (
        <div style={{ marginTop: 18, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)',
          color: '#fca5a5', fontSize: 12.5, lineHeight: 1.4 }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ marginTop: 18, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)',
          color: '#86efac', fontSize: 12.5, lineHeight: 1.4 }}>
          {message}
        </div>
      )}

      <div style={{ marginTop: 22, fontSize: 11, color: 'rgba(220,200,240,0.45)', lineHeight: 1.5 }}>
        by continuing you agree to our <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>terms</a> &amp; <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>privacy notice</a>.
      </div>

      <div style={{
        marginTop: 24, padding: '12px 14px', borderRadius: 12,
        background: 'rgba(34, 197, 94, 0.06)',
        border: '1px solid rgba(34, 197, 94, 0.18)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6l8-3Z" stroke="#86efac" strokeWidth="1.6" strokeLinejoin="round"/>
          <path d="m9 12 2 2 4-4" stroke="#86efac" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: 12, color: 'rgba(187, 247, 208, 0.85)', lineHeight: 1.4 }}>
          your conversations with f8 are <b style={{ color: '#86efac' }}>private</b>. we never train models on them.
        </span>
      </div>
    </>
  );
}

// ─── Email + password ───────────────────────────────────────
function EmailForm({ email, setEmail, password, setPassword, onSubmit, onBack, onForgot, loading, error, message }) {
  const [showPw, setShowPw] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 1;

  return (
    <form onSubmit={onSubmit}>
      <button type="button" onClick={onBack} style={{
        appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent',
        color: 'rgba(220,200,240,0.6)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 24, padding: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>back</span>
      </button>

      <h2 className="login-h2" style={{ margin: 0, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
        log in with email
      </h2>
      <p style={{ margin: '6px 0 28px', color: 'rgba(220,200,240,0.55)', fontSize: 13.5, lineHeight: 1.5 }}>
        for accounts created before we moved to google sign-in.
      </p>

      <label style={{ display: 'block', marginBottom: 14 }}>
        <div style={{
          fontSize: 11, color: 'rgba(220,200,240,0.55)', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
        }}>email</div>
        <input
          autoFocus
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '14px 16px', borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff', fontSize: 14.5, fontFamily: 'inherit', outline: 'none',
            transition: 'all 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(217, 70, 239, 0.5)'; e.target.style.background = 'rgba(217, 70, 239, 0.06)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.04)'; }}
        />
      </label>

      <label style={{ display: 'block' }}>
        <div style={{
          fontSize: 11, color: 'rgba(220,200,240,0.55)', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <span>password</span>
          <button type="button" onClick={onForgot} style={{
            appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
            textTransform: 'none', letterSpacing: 0, fontSize: 11.5,
            color: '#a78bfa', textDecoration: 'none', fontWeight: 500, fontFamily: 'inherit', padding: 0,
          }}>forgot?</button>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type={showPw ? 'text' : 'password'}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '14px 44px 14px 16px', borderRadius: 12,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff', fontSize: 14.5, fontFamily: 'inherit', outline: 'none',
              transition: 'all 0.15s',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(217, 70, 239, 0.5)'; e.target.style.background = 'rgba(217, 70, 239, 0.06)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.04)'; }}
          />
          <button type="button" onClick={() => setShowPw(!showPw)} style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
            padding: 8, color: 'rgba(220,200,240,0.5)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }} aria-label={showPw ? 'hide password' : 'show password'}>
            {showPw ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" stroke="currentColor" strokeWidth="1.6"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M3 3l18 18M10.5 6.3A10 10 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-2.6 3.5M6.5 8.5A14 14 0 0 0 2 12s3.5 6 10 6c1.6 0 3-.4 4.3-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M9.5 9.5a3 3 0 0 0 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      </label>

      {error && (
        <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)',
          color: '#fca5a5', fontSize: 12.5, lineHeight: 1.4 }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)',
          color: '#86efac', fontSize: 12.5, lineHeight: 1.4 }}>
          {message}
        </div>
      )}

      <button type="submit" disabled={!valid || loading} style={{
        appearance: 'none', border: 0, cursor: (valid && !loading) ? 'pointer' : 'default',
        width: '100%', marginTop: 24,
        padding: '14px 16px', borderRadius: 14,
        background: valid ? 'linear-gradient(135deg, #d946ef 0%, #8b5cf6 100%)' : 'rgba(255,255,255,0.06)',
        color: '#fff', fontSize: 14.5, fontWeight: 700, fontFamily: 'inherit',
        boxShadow: valid ? '0 12px 32px rgba(217, 70, 239, 0.4)' : 'none',
        opacity: (valid && !loading) ? 1 : 0.5,
        transition: 'all 0.2s',
      }}>{loading ? 'signing in...' : 'log in →'}</button>

      <div style={{
        marginTop: 22, fontSize: 12.5, color: 'rgba(220,200,240,0.55)', lineHeight: 1.5,
      }}>
        new to pplkpr?{' '}
        {/* PPL-36 — point at the new email signup page rather than nudging
            users back to the google welcome state. */}
        <Link to="/register" style={{
          color: '#a78bfa', fontWeight: 600, textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}>sign up with email →</Link>
      </div>
    </form>
  );
}

// ─── Reset password ─────────────────────────────────────────
function ResetForm({ resetEmail, setResetEmail, onSubmit, onBack, loading, error }) {
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail);
  return (
    <form onSubmit={onSubmit}>
      <button type="button" onClick={onBack} style={{
        appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent',
        color: 'rgba(220,200,240,0.6)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 24, padding: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>back</span>
      </button>

      <h2 className="login-h2" style={{ margin: 0, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
        reset password
      </h2>
      <p style={{ margin: '6px 0 28px', color: 'rgba(220,200,240,0.55)', fontSize: 13.5, lineHeight: 1.5 }}>
        we&apos;ll email you a link to set a new password.
      </p>

      <label style={{ display: 'block' }}>
        <div style={{
          fontSize: 11, color: 'rgba(220,200,240,0.55)', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
        }}>email</div>
        <input
          autoFocus
          type="email"
          required
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          placeholder="you@example.com"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '14px 16px', borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff', fontSize: 14.5, fontFamily: 'inherit', outline: 'none',
            transition: 'all 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(217, 70, 239, 0.5)'; e.target.style.background = 'rgba(217, 70, 239, 0.06)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.04)'; }}
        />
      </label>

      {error && (
        <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)',
          color: '#fca5a5', fontSize: 12.5, lineHeight: 1.4 }}>
          {error}
        </div>
      )}

      <button type="submit" disabled={!valid || loading} style={{
        appearance: 'none', border: 0, cursor: (valid && !loading) ? 'pointer' : 'default',
        width: '100%', marginTop: 24,
        padding: '14px 16px', borderRadius: 14,
        background: valid ? 'linear-gradient(135deg, #d946ef 0%, #8b5cf6 100%)' : 'rgba(255,255,255,0.06)',
        color: '#fff', fontSize: 14.5, fontWeight: 700, fontFamily: 'inherit',
        boxShadow: valid ? '0 12px 32px rgba(217, 70, 239, 0.4)' : 'none',
        opacity: (valid && !loading) ? 1 : 0.5,
        transition: 'all 0.2s',
      }}>{loading ? 'sending...' : 'send reset email →'}</button>
    </form>
  );
}

// ─── Mascot ─────────────────────────────────────────────────
function Mascot({ size = 36 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 10,
      background: 'linear-gradient(135deg, #d946ef, #7c3aed)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 6px 18px rgba(217, 70, 239, 0.35)',
      flexShrink: 0,
    }}>
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="8" r="3" fill="#fff"/>
        <circle cx="16" cy="9" r="2.5" fill="#fff" opacity="0.85"/>
        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        <path d="M13 20c0-2.5 2-4.5 4.5-4.5S22 17.5 22 20" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" opacity="0.85"/>
      </svg>
    </div>
  );
}

function BigOrb() {
  return (
    <div style={{
      position: 'absolute',
      top: '-30%', right: '-10%',
      width: 460, height: 460,
      pointerEvents: 'none', zIndex: 1,
    }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, #f0abfc, #d946ef 35%, #7c3aed 70%, transparent 75%)',
        filter: 'blur(20px)',
        opacity: 0.55,
        animation: 'loginOrbDrift 12s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', inset: '15%', borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, #f0abfc, #d946ef 50%, #7c3aed)',
        boxShadow: '0 0 100px rgba(217, 70, 239, 0.55)',
        opacity: 0.85,
      }} />
    </div>
  );
}

function Aurora() {
  return (
    <>
      <div style={{
        position: 'absolute', top: '40%', left: '-15%',
        width: 480, height: 480, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124, 58, 237, 0.25), transparent 70%)',
        filter: 'blur(40px)',
        animation: 'loginOrbDrift2 18s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-10%', right: '20%',
        width: 380, height: 380, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15), transparent 70%)',
        filter: 'blur(40px)',
        animation: 'loginOrbDrift 22s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.4,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '3px 3px',
        mixBlendMode: 'overlay',
      }} />
    </>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41.8 35.4 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default Login;
